import React, { useState, useEffect } from 'react';
import { api } from '../../src/services/api-new';

// --- Types ---
interface Service {
    id: string;
    name: string;
    description: string;
    price: number;
    duration: number;
    category: string;
}

interface AdminServicesProps {
    services?: Service[];
    onBack?: () => void;
}

const AdminServices: React.FC<AdminServicesProps> = ({ services: externalServices, onBack }) => {
    const [services, setServices] = useState<Service[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingService, setEditingService] = useState<Service | null>(null);
    const [isDataLoading, setIsDataLoading] = useState(true);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        price: '',
        duration: '',
        category: ''
    });

    useEffect(() => {
        const fetchData = async () => {
            if (externalServices && externalServices.length > 0) {
                setServices(externalServices as Service[]);
                setIsDataLoading(false);
                return;
            }

            setIsDataLoading(true);
            try {
                const servicesData = await api.services.getAll();
                setServices(servicesData);
            } catch (error) {
                console.error("Failed to fetch services:", error);
            } finally {
                setIsDataLoading(false);
            }
        };
        fetchData();
    }, [externalServices]);

    const handleAddService = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const result = await api.services.create({
                name: formData.name,
                description: formData.description,
                price: parseFloat(formData.price),
                duration: parseInt(formData.duration),
                category: formData.category
            });
            const newService = result.service || result;
            setServices([...services, newService]);
            setIsAddModalOpen(false);
            resetForm();
        } catch (error) {
            console.error("Failed to add service:", error);
            alert("Failed to add service. Please try again.");
        }
    };

    const handleEditService = (service: Service) => {
        setEditingService(service);
        setFormData({
            name: service.name,
            description: service.description,
            // Ensure values are clean numbers string for input properties
            price: (service.price?.toString() || '0').replace(/[^0-9.]/g, ''),
            duration: (service.duration?.toString() || '0').replace(/[^0-9]/g, ''),
            category: service.category
        });
        setIsEditModalOpen(true);
    };

    const handleUpdateService = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingService) return;

        try {
            await api.services.update(editingService.id, {
                name: formData.name,
                description: formData.description,
                price: parseFloat(formData.price),
                duration: parseInt(formData.duration),
                category: formData.category
            });

            const updatedServices = services.map(service =>
                service.id === editingService.id
                    ? { ...service, ...formData, price: parseFloat(formData.price), duration: parseInt(formData.duration) }
                    : service
            );
            setServices(updatedServices);
            setIsEditModalOpen(false);
            setEditingService(null);
            resetForm();
        } catch (error) {
            console.error("Failed to update service:", error);
        }
    };

    const handleDeleteService = async (id: string) => {
        if (!confirm('Are you sure you want to delete this service?')) return;

        try {
            await api.services.delete(id);
            setServices(services.filter(service => service.id !== id));
        } catch (error) {
            console.error("Failed to delete service:", error);
        }
    };

    const resetForm = () => {
        setFormData({ name: '', description: '', price: '', duration: '', category: '' });
    };

    const filteredServices = services.filter(service =>
        (service.name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
        (service.description?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
        (service.category?.toLowerCase() || '').includes(searchQuery.toLowerCase())
    );

    if (isDataLoading) {
        return (
            <div className="h-full flex items-center justify-center bg-slate-50 dark:bg-background-dark">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
                    <p className="text-[10px] font-black text-primary uppercase tracking-[0.2em]">Loading Services...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="h-full overflow-y-auto bg-slate-50 dark:bg-background-dark p-6 lg:p-10 scroll-smooth">
            <div className="max-w-7xl mx-auto space-y-10">

                {/* Header */}
                <header className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                    <div className="flex items-center gap-4">
                        {onBack && (
                            <button
                                onClick={onBack}
                                className="flex items-center justify-center w-10 h-10 rounded-full bg-white dark:bg-surface-dark border border-gray-100 dark:border-gray-800 hover:border-primary transition-all shadow-sm"
                            >
                                <span className="material-icons-round text-gray-600 dark:text-gray-300">arrow_back</span>
                            </button>
                        )}
                        <div>
                            <nav className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">
                                <span>Admin Panel</span>
                                <span className="material-icons-round text-xs">chevron_right</span>
                                <span>Services Management</span>
                            </nav>
                            <h1 className="text-4xl font-black text-primary-dark dark:text-white tracking-tight uppercase">Services</h1>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => setIsAddModalOpen(true)}
                            className="bg-primary hover:bg-primary-dark text-white px-6 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl shadow-primary/30 transition-all flex items-center gap-2 active:scale-95"
                        >
                            <span className="material-icons-round text-sm">add</span> Add Service
                        </button>
                    </div>
                </header>

                {/* Search Bar */}
                <div className="bg-white dark:bg-surface-dark p-6 rounded-[2.5rem] shadow-xl shadow-black/5 border border-gray-100 dark:border-gray-800">
                    <div className="relative">
                        <span className="material-icons-round absolute left-6 top-1/2 -translate-y-1/2 text-gray-400">search</span>
                        <input
                            type="text"
                            placeholder="Search services by name, description, or category..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-16 pr-6 py-4 bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 focus:bg-white dark:focus:bg-surface-dark rounded-2xl text-sm font-medium outline-none transition-all"
                        />
                    </div>
                </div>

                {/* Services Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredServices.map((service) => (
                        <div key={service.id} className="bg-white dark:bg-surface-dark p-6 rounded-[2.5rem] shadow-xl shadow-black/5 border border-gray-100 dark:border-gray-800 hover:border-primary/50 transition-all group">
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h3 className="text-lg font-black text-primary-dark dark:text-white tracking-tight">{service.name}</h3>
                                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{service.category}</p>
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => handleEditService(service)}
                                        className="w-8 h-8 rounded-xl bg-primary/10 text-primary hover:bg-primary/20 transition-all flex items-center justify-center"
                                    >
                                        <span className="material-icons-round text-sm">edit</span>
                                    </button>
                                    <button
                                        onClick={() => handleDeleteService(service.id)}
                                        className="w-8 h-8 rounded-xl bg-red-500/10 text-red-500 hover:bg-red-500/20 transition-all flex items-center justify-center"
                                    >
                                        <span className="material-icons-round text-sm">delete</span>
                                    </button>
                                </div>
                            </div>

                            <p className="text-sm text-gray-600 dark:text-gray-300 font-medium leading-relaxed mb-4">{service.description}</p>

                            <div className="flex justify-between items-center">
                                <div>
                                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Price</p>
                                    <p className="text-lg font-black text-primary-dark dark:text-white">${service.price}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Duration</p>
                                    <p className="text-sm font-black text-gray-600 dark:text-gray-300">{service.duration} min</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {filteredServices.length === 0 && (
                    <div className="text-center py-12">
                        <span className="material-icons-round text-6xl text-gray-300 mb-4">medical_services</span>
                        <h3 className="text-lg font-black text-gray-400 uppercase tracking-widest mb-2">No Services Found</h3>
                        <p className="text-sm text-gray-500">{searchQuery ? 'Try adjusting your search terms' : 'No services have been added yet'}</p>
                    </div>
                )}
            </div>

            {/* Add Service Modal */}
            {isAddModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-primary-dark/40 backdrop-blur-xl animate-fade-in" onClick={() => setIsAddModalOpen(false)}>
                    <div className="bg-white dark:bg-surface-dark w-full max-w-md rounded-[3rem] shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-scale-up" onClick={e => e.stopPropagation()}>
                        <div className="p-10 border-b border-gray-100 dark:border-gray-800 bg-slate-50/50 dark:bg-black/20 flex justify-between items-center">
                            <div>
                                <p className="text-[10px] font-black text-primary uppercase tracking-[0.3em] mb-1">New Entry</p>
                                <h2 className="text-3xl font-black text-primary-dark dark:text-white tracking-tight">Add Service</h2>
                            </div>
                            <button onClick={() => setIsAddModalOpen(false)} className="w-12 h-12 rounded-2xl hover:bg-white dark:hover:bg-gray-700 transition-colors flex items-center justify-center text-gray-400"><span className="material-icons-round">close</span></button>
                        </div>

                        <form id="add-service-form" onSubmit={handleAddService} className="flex-1 overflow-y-auto p-10 space-y-6 custom-scrollbar">
                            <div>
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Service Name</label>
                                <input
                                    type="text"
                                    required
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 focus:bg-white p-5 rounded-2xl text-lg font-black outline-none transition-all placeholder:text-gray-300"
                                    placeholder="e.g. Annual Checkup"
                                />
                            </div>

                            <div>
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Description</label>
                                <textarea
                                    required
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    rows={3}
                                    className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 focus:bg-white p-5 rounded-2xl text-sm font-medium outline-none transition-all resize-none"
                                    placeholder="Describe the service..."
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Price ($)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        required
                                        value={formData.price}
                                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                                        className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 p-5 rounded-2xl text-sm font-black outline-none transition-all"
                                        placeholder="0.00"
                                    />
                                </div>
                                <div>
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Duration (min)</label>
                                    <input
                                        type="number"
                                        required
                                        value={formData.duration}
                                        onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                                        className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 p-5 rounded-2xl text-sm font-black outline-none transition-all"
                                        placeholder="30"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Category</label>
                                <select
                                    required
                                    value={formData.category}
                                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                    className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 p-5 rounded-2xl text-sm font-black outline-none transition-all"
                                >
                                    <option value="">Select category</option>
                                    <option value="Vaccination">Vaccination</option>
                                    <option value="Surgery">Surgery</option>
                                    <option value="Dental">Dental</option>
                                    <option value="General">General</option>
                                    <option value="Emergency">Emergency</option>
                                    <option value="Diagnostic">Diagnostic</option>
                                </select>
                            </div>
                        </form>

                        <div className="p-10 border-t border-gray-100 dark:border-gray-800 flex justify-end gap-4 bg-slate-50/50 dark:bg-black/20">
                            <button type="button" onClick={() => setIsAddModalOpen(false)} className="px-8 py-4 font-black text-[10px] text-gray-400 uppercase tracking-widest hover:text-gray-600 transition-colors">Cancel</button>
                            <button type="submit" form="add-service-form" className="bg-primary text-white px-10 py-5 rounded-3xl font-black text-[10px] uppercase tracking-[0.2em] shadow-2xl shadow-primary/30 transition-all hover:-translate-y-1 active:scale-95">Add Service</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Service Modal */}
            {isEditModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-primary-dark/40 backdrop-blur-xl animate-fade-in" onClick={() => setIsEditModalOpen(false)}>
                    <div className="bg-white dark:bg-surface-dark w-full max-w-md rounded-[3rem] shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-scale-up" onClick={e => e.stopPropagation()}>
                        <div className="p-10 border-b border-gray-100 dark:border-gray-800 bg-slate-50/50 dark:bg-black/20 flex justify-between items-center">
                            <div>
                                <p className="text-[10px] font-black text-primary uppercase tracking-[0.3em] mb-1">Edit Entry</p>
                                <h2 className="text-3xl font-black text-primary-dark dark:text-white tracking-tight">Edit Service</h2>
                            </div>
                            <button onClick={() => setIsEditModalOpen(false)} className="w-12 h-12 rounded-2xl hover:bg-white dark:hover:bg-gray-700 transition-colors flex items-center justify-center text-gray-400"><span className="material-icons-round">close</span></button>
                        </div>

                        <form id="edit-service-form" onSubmit={handleUpdateService} className="flex-1 overflow-y-auto p-10 space-y-6 custom-scrollbar">
                            <div>
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Service Name</label>
                                <input
                                    type="text"
                                    required
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 focus:bg-white p-5 rounded-2xl text-lg font-black outline-none transition-all placeholder:text-gray-300"
                                    placeholder="e.g. Annual Checkup"
                                />
                            </div>

                            <div>
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Description</label>
                                <textarea
                                    required
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    rows={3}
                                    className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 focus:bg-white p-5 rounded-2xl text-sm font-medium outline-none transition-all resize-none"
                                    placeholder="Describe the service..."
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Price ($)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        required
                                        value={formData.price}
                                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                                        className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 p-5 rounded-2xl text-sm font-black outline-none transition-all"
                                        placeholder="0.00"
                                    />
                                </div>
                                <div>
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Duration (min)</label>
                                    <input
                                        type="number"
                                        required
                                        value={formData.duration}
                                        onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                                        className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 p-5 rounded-2xl text-sm font-black outline-none transition-all"
                                        placeholder="30"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Category</label>
                                <select
                                    required
                                    value={formData.category}
                                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                    className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 p-5 rounded-2xl text-sm font-black outline-none transition-all"
                                >
                                    <option value="">Select category</option>
                                    <option value="Vaccination">Vaccination</option>
                                    <option value="Surgery">Surgery</option>
                                    <option value="Dental">Dental</option>
                                    <option value="General">General</option>
                                    <option value="Emergency">Emergency</option>
                                    <option value="Diagnostic">Diagnostic</option>
                                </select>
                            </div>
                        </form>

                        <div className="p-10 border-t border-gray-100 dark:border-gray-800 flex justify-end gap-4 bg-slate-50/50 dark:bg-black/20">
                            <button type="button" onClick={() => setIsEditModalOpen(false)} className="px-8 py-4 font-black text-[10px] text-gray-400 uppercase tracking-widest hover:text-gray-600 transition-colors">Cancel</button>
                            <button type="submit" form="edit-service-form" className="bg-primary text-white px-10 py-5 rounded-3xl font-black text-[10px] uppercase tracking-[0.2em] shadow-2xl shadow-primary/30 transition-all hover:-translate-y-1 active:scale-95">Update Service</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminServices;